import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { debounce, isNumber } from 'lodash-es';
import { VList, type VListHandle } from 'virtua';

import type { AssetsSegmentSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import { segmentColors } from '@iconik-io/shared-lib/src/react/components/shared/segment/constants';
import { useVirtualizedScrollToIds } from '@iconik-io/shared-lib/src/react/hooks/use_virtualized_scroll_to_ids';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';

import { AssetSegmentForm } from '../../components/asset_segment_form/asset_segment_form';
import DeleteTimeBasedMetadata from '../../components/modals/delete_objects/time_based_metadata';
import ModalsManager from '../../components/modals/modals_manager/modals_manager';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import Segment from '../../components/segment/segment';
import {
  SEGMENT_FORM_TYPE_TIME_BASED_METADATA,
  SEGMENT_TYPE_GENERIC,
  SEGMENT_TYPE_TAG,
} from '../../components/segment_form/constants';
import SelectFieldColorOption from '../../components/select_field_custom_options/option_color_component/option_color_component';
import SelectFieldColorValue from '../../components/select_field_custom_values/select_field_color_value/select_field_color_value';
import { TIME_BASED_METADATA } from '../../constants/help_urls';
import { DELETE_TIME_BASED_METADATA_MODAL } from '../../constants/modals';
import { setPropertyFilter } from '../../ducks/asset_time_based_metadata/slice';
import {
  addMetadataValuesForTags,
  searchTimebasedMetadata,
  updateSegment,
  updateStateSegments,
} from '../../ducks/asset_time_based_metadata/thunks';
import type { TimebasedMetadataFilters } from '../../ducks/asset_time_based_metadata/types';
import { warningMessage } from '../../ducks/messages/thunks';
import { putAssetSegmentMetadataValues } from '../../ducks/metadata_values/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  selectCurrentVersionId,
  selectIsLatestVersion,
} from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import SelectFieldWrapper from '../../ui/forms/field_wrappers/select/select';
import SelectMetadataViewFieldWrapper from '../../ui/forms/field_wrappers/select_metadata_view/select_metadata_view';
import SelectFieldMultiWrapper from '../../ui/forms/field_wrappers/select_multi/select_multi';
import Form from '../../ui/forms/form';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import SearchInput from '../../ui/search_input/search_input';
import type { SelectOption } from '../../ui/select_field/types';
import { selectUserRoles } from '../../utils/get_user_roles';
import {
  getTimebasedMetadataFilters,
  selectSegmentIds,
  selectTagMetadataView,
} from './selectors';

import styles from '../asset_comments/asset_comments.module.css';

export const AssetTimeBasedMetadataPanel: FC = () => {
  const [isFormOpened, setIsFormOpened] = useState(false);

  const dispatch = useAppDispatch();
  const timebasedMetadataLoading = useAppSelector(
    (state) => state.assetTimeBasedMetadataReducer.loading,
  );
  const highlightedSegmentIds = useAppSelector(
    (state) => state.segments.highlightedSegmentIds,
  );
  const { id: assetId, versionId } = useParams({ strict: false });
  const asset = useAppSelector((state) =>
    assetId ? state.entities.assets[assetId] : undefined,
  );
  const segments = useAppSelector((state) => state.entities.segments);
  const timeBasedMetadataIds = useAppSelector((state) =>
    selectSegmentIds(state, assetId, versionId),
  );
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, versionId),
  );
  const tagMetadataView = useAppSelector(selectTagMetadataView);
  const filters = useAppSelector(getTimebasedMetadataFilters);
  const roles = useAppSelector(selectUserRoles);
  const isLatestVersion = useAppSelector((state) =>
    selectIsLatestVersion(state, assetId, versionId),
  );

  const vListRef = useRef<VListHandle>(null);
  const { setScrollToIds } = useVirtualizedScrollToIds({
    virtualizer: vListRef.current,
    ids: timeBasedMetadataIds,
  });

  useEffect(() => {
    if (highlightedSegmentIds.length === 0) {
      return;
    }
    setScrollToIds(highlightedSegmentIds);
  }, [highlightedSegmentIds]);

  const handleEditMode = useCallback(
    (segmentId: string, metadataViewId?: string | null) => {
      const viewId = metadataViewId ?? tagMetadataView?.id;
      if (!viewId || !assetId) {
        return;
      }
      dispatch(addMetadataValuesForTags(assetId, segmentId, viewId));
    },
    [tagMetadataView?.id, assetId],
  );

  const handleEdit = useCallback(
    (
      originalSegment: SegmentEntityWithIdNormalized,
      values: Partial<AssetsSegmentSchemaPatched>,
    ) => {
      const { metadata, ...segmentData } = values;
      const metadataViewId =
        originalSegment.metadata_view_id ?? tagMetadataView?.id;
      const promises: Promise<unknown>[] = [];
      if (asset?.type === 'SUBCLIP') {
        // Validate that we are not trying to change the in and out points outside the subclip
        if (
          isNumber(asset.time_start_milliseconds) &&
          isNumber(segmentData.time_start_milliseconds) &&
          segmentData.time_start_milliseconds < asset.time_start_milliseconds
        ) {
          dispatch(
            warningMessage({
              text: 'In point smaller than subclip start time.',
            }),
          );
          return Promise.resolve();
        }
        if (
          isNumber(asset.time_end_milliseconds) &&
          isNumber(segmentData.time_end_milliseconds) &&
          segmentData.time_end_milliseconds > asset.time_end_milliseconds
        ) {
          dispatch(
            warningMessage({
              text: 'Out point greater than subclip end time.',
            }),
          );
          return Promise.resolve();
        }
      }
      if (!assetId) {
        return Promise.resolve();
      }
      promises.push(
        dispatch(updateSegment(assetId, originalSegment.id, segmentData)),
      );
      if (metadataViewId && metadata) {
        promises.push(
          dispatch(
            putAssetSegmentMetadataValues(
              assetId,
              originalSegment.id,
              metadataViewId,
              metadata,
            ),
          ),
        );
      }

      dispatch(
        updateStateSegments(assetId, [
          {
            id: originalSegment.id,
            ...values,
          },
        ]),
      );

      return Promise.all(promises);
    },
    [assetId, asset, tagMetadataView?.id],
  );

  const handleDelete = useCallback(
    (segmentId: string) => {
      const item = segments[segmentId];

      dispatch(
        openModal({
          id: DELETE_TIME_BASED_METADATA_MODAL,
          contextItems: [item],
        }),
      );
    },
    [segments],
  );

  const handleSearch = useCallback(
    (query: string) => {
      if (!assetId) {
        return;
      }
      dispatch(searchTimebasedMetadata(assetId, versionId, query));
    },
    [assetId, versionId],
  );

  const handleFilter = useCallback(
    (name: keyof TimebasedMetadataFilters, value: string[]) => {
      dispatch(setPropertyFilter({ name, value }));
    },
    [],
  );

  const handleSegmentCreated = useCallback(() => {
    setIsFormOpened(false);
  }, []);

  const handleDebouncedSearch = debounce(
    (query: string) => handleSearch(query),
    400,
  );

  const handleCancel = useCallback(() => setIsFormOpened(false), []);

  if (!assetId) {
    return null;
  }

  const timebasedMetadataElements = timeBasedMetadataIds.map((id, index) => {
    const segment = segments[id];
    const className = classNames({
      [styles.highlight]: highlightedSegmentIds.includes(segment.id),
      [styles.first_comment]: index === 0,
    });
    const isSubclip = asset?.type === 'SUBCLIP';

    return (
      <div key={segment.id}>
        <Segment
          assetId={assetId}
          segment={segment}
          className={className}
          onEditMode={handleEditMode}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isCurrentVersion={
            !currentVersionId ||
            currentVersionId === segment.version_id ||
            !segment.version_id ||
            (isSubclip && segment.version_id === asset?.original_version_id)
          }
        />
      </div>
    );
  });

  /*
   * Use strict inequality check because timebasedMetadataLoading[assetId]
   * is undefined before loading starts.
   */
  const isLoading = assetId
    ? timebasedMetadataLoading[assetId] !== false
    : false;

  const timeBasedMetadataContent =
    timeBasedMetadataIds.length > 0 ? (
      <div className={styles.autosize_wrapper}>
        <VList ref={vListRef}>{timebasedMetadataElements}</VList>
      </div>
    ) : (
      <NoContentPanel
        title="Add metadata specific to particular segments of multi-frame assets"
        linkText="Learn more"
        linkHref={TIME_BASED_METADATA}
      />
    );

  return (
    <>
      <ModalsManager
        modals={{
          [DELETE_TIME_BASED_METADATA_MODAL]: DeleteTimeBasedMetadata,
        }}
      />
      <div
        className={classNames(styles.list_wrapper, {
          [styles.list_wrapper_disabled]: isFormOpened,
        })}
      >
        <div className={styles.header}>
          <SearchInput
            onSearch={handleDebouncedSearch}
            className={styles.search}
          />
          <Form className={styles.filter_fields}>
            <div className={styles.search_fields}>
              <div className={styles.search_fields_row}>
                <div className={styles.color_search}>
                  <SelectFieldMultiWrapper<SelectOption>
                    isMulti
                    name="segment_color"
                    placeholder="Color"
                    onChange={(value) => {
                      const values = value ? value.map((v) => v.value) : [];
                      handleFilter('segment_color', values);
                    }}
                    options={segmentColors}
                    className={styles.search_field}
                    valueComponent={SelectFieldColorValue}
                    optionComponent={SelectFieldColorOption}
                  />
                </div>
                <div className={styles.sort_by}>
                  <SelectFieldWrapper
                    canClear
                    options={[
                      { label: 'Metadata Views', value: SEGMENT_TYPE_GENERIC },
                      { label: 'Tags', value: SEGMENT_TYPE_TAG },
                    ]}
                    onChange={(value) => {
                      handleFilter('segment_type', value ? [value] : []);
                    }}
                    name="segment_type"
                    className={styles.search_field}
                  />
                </div>
              </div>
              {filters.segment_type?.includes(SEGMENT_TYPE_GENERIC) && (
                <SelectMetadataViewFieldWrapper
                  name="metadata_view"
                  isMulti={false}
                  objectType="segments"
                  category="generic"
                  canClear
                  onChange={(value) => {
                    handleFilter('view_id', value ? [value] : []);
                  }}
                />
              )}
            </div>
          </Form>
        </div>
        {isLoading ? <LoadingBar /> : timeBasedMetadataContent}
      </div>
      {roles.canCreateSegments && isLatestVersion ? (
        <div className={styles.segment_form_wrapper}>
          {isFormOpened ? (
            <AssetSegmentForm
              formClassName={styles.segment_form}
              formType={SEGMENT_FORM_TYPE_TIME_BASED_METADATA}
              onSegmentCreated={handleSegmentCreated}
              onCancel={handleCancel}
            />
          ) : (
            <Button
              className={styles.open_segment_form_button}
              onClick={() => setIsFormOpened(true)}
              wideBasis
            >
              Add time-based metadata
            </Button>
          )}
        </div>
      ) : null}
    </>
  );
};
